import { useCart } from "../../../context/CartContext";
import { useAuth } from "../../../context/AuthContext";
import { X, Minus, Plus, Trash2, ShoppingBag, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { orderService } from '../../../api/service/order.service';
import { showToast } from '../notifications/showToast';
import { CreateOrderPayload } from "../../../api/interface/orders.interface";

export const CartDrawer = () => {
  const { items, isCartOpen, closeCart, updateQuantity, removeItem, totalItems, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{ nom?: string; tel?: string; adresse?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isCartOpen) return null;

  // ✅ Nettoyage minimal : on enlève les espaces et on ajoute un '+' si absent
  const cleanPhoneForApi = (raw: string): string => {
    let cleaned = raw.replace(/\s/g, '');
    if (cleaned && !cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  const handleProceedToCheckout = async () => {
    const nomInput = document.getElementById('checkout-nom') as HTMLInputElement;
    const telInput = document.getElementById('checkout-tel') as HTMLInputElement;
    const adresseInput = document.getElementById('checkout-adresse') as HTMLInputElement;

    const nom = nomInput?.value.trim() || '';
    const telRaw = telInput?.value.trim() || '';
    const adresse = adresseInput?.value.trim() || '';

    const newErrors: { nom?: string; tel?: string; adresse?: string } = {};

    if (!nom) {
      newErrors.nom = 'Le nom complet est requis';
    }
    if (!telRaw) {
      newErrors.tel = 'Le numéro de téléphone est requis';
    }
    if (!adresse) {
      newErrors.adresse = "L'adresse de livraison est requise";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.nom) nomInput?.focus();
      else if (newErrors.tel) telInput?.focus();
      else if (newErrors.adresse) adresseInput?.focus();

      showToast({
        title: 'Champs manquants',
        description: 'Veuillez remplir tous les champs obligatoires.',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    if (items.length === 0) {
      showToast({
        title: 'Panier vide',
        description: 'Ajoutez des produits avant de passer commande.',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    const phoneForApi = cleanPhoneForApi(telRaw);

    const orderItems = items.map(item => ({
      product_id: String(item.id),
      quantity: item.quantity,
    }));

    const payload: CreateOrderPayload = {
      items: orderItems,
      customer_name: nom,
      customer_phone: phoneForApi,
      customer_address: adresse,
    };

    setIsSubmitting(true);

    try {
      const response = await orderService.createOrder(payload);
      const order = response.data;

      showToast({
        title: 'Commande créée avec succès',
        description: 'Votre commande a été enregistrée. Redirection en cours...',
        type: 'success',
        duration: 3000,
      });

      clearCart();
      closeCart();
      navigate(`/checkout?order_id=${order.id}`, { replace: true });
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Une erreur est survenue lors de la création de la commande.';
      showToast({
        title: 'Erreur',
        description: message,
        type: 'error',
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={closeCart}
      />

      <div className="fixed right-0 md:right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out animate-slide-in-right">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingBag size={24} className="text-accent" />
            <h2 className="text-xl font-bold text-gray-800">Mon panier</h2>
            <span className="bg-gray-100 text-gray-600 text-sm px-2 py-0.5 rounded-full">
              {totalItems} article{totalItems > 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={closeCart}
            className="p-1 rounded-full hover:bg-gray-100 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 340px)' }}>
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Votre panier est vide</p>
              <button
                onClick={() => {
                  closeCart();
                  navigate('/products');
                }}
                className="mt-4 bg-accent p-2 rounded-xl text-primary font-semibold hover:underline"
              >
                Continuer mes achats
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 border-b pb-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                  />

                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="font-semibold text-gray-800 text-sm truncate">
                      {item.title}
                    </h3>
                    <p className="text-accent font-bold text-sm mt-1">
                      {item.price ? `${item.price.toLocaleString()} FCFA` : 'Prix sur devis'}
                    </p>

                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 rounded-full hover:bg-gray-100 transition"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="font-medium w-6 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 rounded-full hover:bg-gray-100 transition"
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="ml-auto p-1 rounded-full hover:bg-red-50 text-red-500 transition flex-shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-2">
            <div className="mb-4 space-y-3 bg-gray-200 p-3 rounded-lg">
              <h3 className="font-semibold text-black text-xl flex items-center gap-2">
                Coordonnées
              </h3>

              {/* Champ Nom */}
              <div>
                <input
                  id="checkout-nom"
                  type="text"
                  placeholder="Nom complet *"
                  defaultValue={user?.first_name || ''}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent ${
                    errors.nom ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-accent'
                  }`}
                  onChange={() => {
                    if (errors.nom) setErrors(prev => ({ ...prev, nom: undefined }));
                  }}
                />
                {errors.nom && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.nom}
                  </p>
                )}
              </div>

              {/* Champ Téléphone simplifié */}
              <div>
                <input
                  id="checkout-tel"
                  type="tel"
                  placeholder="tel: +2290153651890"
                  defaultValue={user?.phone_number || ''}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent ${
                    errors.tel ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-accent'
                  }`}
                  onChange={() => {
                    if (errors.tel) setErrors(prev => ({ ...prev, tel: undefined }));
                  }}
                />
                {errors.tel && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.tel}
                  </p>
                )}
              </div>

              {/* Champ Adresse */}
              <div>
                <input
                  id="checkout-adresse"
                  type="text"
                  placeholder="Adresse de livraison *"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent ${
                    errors.adresse ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-accent'
                  }`}
                  onChange={() => {
                    if (errors.adresse) setErrors(prev => ({ ...prev, adresse: undefined }));
                  }}
                />
                {errors.adresse && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.adresse}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between mb-4">
              <span className="font-semibold text-gray-800">Total</span>
              <span className="font-bold text-accent text-xl">
                {totalPrice.toLocaleString()} FCFA
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={clearCart}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-red-300 text-red-600 rounded-full hover:bg-red-50 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Vider
              </button>
              <button
                onClick={handleProceedToCheckout}
                disabled={isSubmitting}
                className={`flex-1 bg-accent text-primary font-bold px-4 py-2 rounded-full shadow hover:shadow-lg transition flex items-center justify-center gap-2 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Commande...
                  </>
                ) : (
                  "Procéder à l'achat"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};